import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Dumbbell, Trophy, UserPlus, MessageSquare, Layers,
  CreditCard, Utensils, Heart, BarChart3, Activity, Upload, Download, Loader2, Archive
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { downloadJson } from '../../utils/downloadJson';

// Collections that have dedicated import/export endpoints
const EXPORTABLE = {
  users: { endpoint: '/admin/users/export', key: 'users', filename: 'users-backup' },
  workouts: { endpoint: '/admin/workouts/export', key: 'workouts', filename: 'workouts-backup' },
  challenges: { endpoint: '/admin/challenges/export', key: 'challenges', filename: 'challenges-backup' },
  follows: { endpoint: '/admin/follows/export', key: 'follows', filename: 'follows-backup' },
  challenge_participants: { endpoint: '/admin/challenge-participants/export', key: 'participants', filename: 'challenge-participants-backup' },
};

const GROUPS = [
  {
    title: 'Community',
    icon: Users,
    color: 'text-primary',
    cards: [
      { key: 'users', label: 'Users', icon: Users },
      { key: 'follows', label: 'Follows', icon: UserPlus },
      { key: 'buddy_relationships', label: 'Buddies', icon: Heart },
      { key: 'fitness_groups', label: 'Groups', icon: Layers },
    ],
  },
  {
    title: 'Content',
    icon: Dumbbell,
    color: 'text-green-400',
    cards: [
      { key: 'workouts', label: 'Workouts', icon: Dumbbell },
      { key: 'programs', label: 'Programs', icon: Layers },
      { key: 'challenges', label: 'Challenges', icon: Trophy },
      { key: 'nutrition_tips', label: 'Nutrition Tips', icon: Utensils },
    ],
  },
  {
    title: 'Social Feed',
    icon: MessageSquare,
    color: 'text-blue-400',
    cards: [
      { key: 'posts', label: 'Posts', icon: MessageSquare },
      { key: 'comments', label: 'Comments', icon: MessageSquare },
      { key: 'likes', label: 'Likes', icon: Heart },
      { key: 'direct_messages', label: 'Direct Messages', icon: MessageSquare },
    ],
  },
  {
    title: 'Activity',
    icon: Activity,
    color: 'text-orange-400',
    cards: [
      { key: 'workout_logs', label: 'Workout Logs', icon: Activity },
      { key: 'challenge_participants', label: 'Challenge Entries', icon: Trophy },
      { key: 'weight_entries', label: 'Weight Logs', icon: BarChart3 },
      { key: 'wearable_data', label: 'Wearable Data', icon: Activity },
    ],
  },
  {
    title: 'Commerce',
    icon: CreditCard,
    color: 'text-yellow-400',
    cards: [
      { key: 'user_purchases', label: 'Program Sales', icon: CreditCard },
      { key: 'payment_transactions', label: 'Transactions', icon: CreditCard },
      { key: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
      { key: 'user_nutrition_plans', label: 'Nutrition Plans Sold', icon: Utensils },
    ],
  },
];

export const AdminDataTab = ({ api, onBulkImportUsers, onBulkImportWorkouts, onBulkImportFollows, onBulkImportChallenges, onBulkImportChallengeParticipants }) => {
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [fullBackupLoading, setFullBackupLoading] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await api().get('/admin/data-summary');
      setSummary(res.data.summary || {});
    } catch (err) {
      toast.error('Failed to load data summary');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSummary();
  }, []); // eslint-disable-line

  const handleExport = async (key) => {
    const cfg = EXPORTABLE[key];
    if (!cfg) return;
    setExporting(key);
    try {
      const res = await api().get(cfg.endpoint);
      const items = res.data[cfg.key] || [];
      if (!items.length) {
        toast.error(`No ${cfg.key} to export`);
      } else {
        downloadJson(items, cfg.filename);
        toast.success(`Exported ${items.length} ${cfg.key}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to export ${cfg.key}`);
    }
    setExporting(null);
  };

  const importHandlers = {
    users: onBulkImportUsers,
    workouts: onBulkImportWorkouts,
    follows: onBulkImportFollows,
    challenges: onBulkImportChallenges,
    challenge_participants: onBulkImportChallengeParticipants,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const grandTotal = Object.values(summary).reduce((a, b) => a + (b || 0), 0);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
      data-testid="admin-data-tab"
    >
      <div className="bg-card border border-border p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl text-white uppercase tracking-wider">Your Data</h2>
          <p className="text-muted-foreground text-sm mt-1">
            {grandTotal.toLocaleString()} total records across {Object.keys(summary).length} collections
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={async () => {
              setFullBackupLoading(true);
              try {
                const res = await api().get('/admin/full-backup');
                downloadJson(res.data, 'full-backup');
                const counts = res.data.counts || {};
                const totalCount = Object.values(counts).reduce((a, b) => a + b, 0);
                toast.success(`Backed up ${totalCount} records across ${Object.keys(counts).length} collections`);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Full backup failed');
              }
              setFullBackupLoading(false);
            }}
            disabled={fullBackupLoading}
            className="bg-primary text-black hover:bg-primary/90 font-heading tracking-wider uppercase"
            data-testid="full-backup-btn"
          >
            {fullBackupLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Archive className="h-4 w-4 mr-2" />
            )}
            Full Backup
          </Button>
          <Button
            onClick={fetchSummary}
            variant="outline"
            className="border-border text-white hover:bg-white/5"
            data-testid="refresh-data-summary-btn"
          >
            Refresh
          </Button>
        </div>
      </div>

      {GROUPS.map((group) => {
        const Icon = group.icon;
        return (
          <div key={group.title}>
            <div className="flex items-center gap-2 mb-3">
              <Icon className={`h-5 w-5 ${group.color}`} />
              <h3 className={`font-heading text-lg uppercase tracking-wider ${group.color}`}>{group.title}</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {group.cards.map(({ key, label, icon: CardIcon }) => {
                const count = summary[key] || 0;
                const canExport = !!EXPORTABLE[key];
                const canImport = !!importHandlers[key];
                return (
                  <div
                    key={key}
                    className="bg-card border border-border p-4 flex flex-col justify-between gap-3"
                    data-testid={`data-card-${key}`}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <CardIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground text-xs uppercase tracking-wider">{label}</span>
                      </div>
                      <div className="text-3xl font-heading text-white">{count.toLocaleString()}</div>
                    </div>
                    {(canExport || canImport) && (
                      <div className="flex gap-1 flex-wrap">
                        {canImport && (
                          <Button
                            onClick={() => importHandlers[key]()}
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs border-border text-white hover:bg-white/5"
                            data-testid={`data-import-${key}`}
                          >
                            <Upload className="h-3 w-3 mr-1" /> Import
                          </Button>
                        )}
                        {canExport && (
                          <Button
                            onClick={() => handleExport(key)}
                            disabled={exporting === key}
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs border-border text-white hover:bg-white/5"
                            data-testid={`data-export-${key}`}
                          >
                            {exporting === key ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <Download className="h-3 w-3 mr-1" />
                            )}
                            Export
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </motion.div>
  );
};
